import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NewVisitModal } from '@/components/visits/NewVisitModal';
import { AddCalendarEventModal } from '@/components/calendar/AddCalendarEventModal';
import { EditCustomerModal } from '@/components/customers/EditCustomerModal';
import { CustomerTag, CustomerTagList } from '@/components/ui/customer-tag';
import { getTagsByCategory, TAG_CATEGORIES } from '@/lib/customer-tags';
import { useCalendar } from '@/hooks/use-calendar';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  Package, 
  ShoppingBag, 
  Camera, 
  FileText, 
  Clock,
  MapPin,
  Star,
  Shield,
  Plus,
  Eye,
  Trash2,
  Play,
  Edit,
  Loader2,
  UserCheck
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { useAuth } from '@/hooks/use-auth';
import { useKvkkStatus } from '@/hooks/use-kvkk-status';
import { useToast } from '@/hooks/use-toast';
import { KvkkConsentSection } from '@/components/kvkk/KvkkConsentSection';

type CustomerRow = Tables<'customers'>;
type VisitRow = Tables<'visits'>;
type SaleRow = Tables<'sales'>;
type PackageRow = Tables<'packages'>;

interface CustomerDetail {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar?: string;
  status: 'active' | 'inside' | 'vip';
  colorTag: CustomerRow['color'];
  consent: boolean;
  stats: {
    totalVisits: number;
    avgDuration: string;
    frequency: string;
    totalSpent: string;
  };
  customer_data: CustomerRow;
}

interface Visit {
  id: string;
  date: string;
  visitedAt: string | null;
  location: string;
  duration: string;
  photos?: string[];
  products?: string[];
  notes?: string;
}

interface Package {
  id: string;
  title: string;
  status: string;
  total: string;
  items: Array<{
    id: string;
    name: string;
    quantity: number;
    unit_price: number;
  }>;
  created_at: string;
}

interface Sale {
  id: string;
  date: string;
  saleDatetime?: string; // Raw ISO datetime for accurate calculations
  total: string;
  items: string;
  productNames: string[];
  paymentStatus: 'paid' | 'partial' | 'pending' | 'overdue';
  totalPaid: number;
  balanceDue: number;
  itemCount: number;
  isOnHold?: boolean;
  actualSaleId?: string;
  linkedSaleId?: string | null;
  status?: 'completed' | 'cancelled' | 'open' | 'refunded';
}

interface PossibleOldMatch {
  visitId: string;
  visitFaceId: string;
  visitAt: string;
  similarity: number;
  qualityScore: number | null;
  snapshotPath: string | null;
}

const POSSIBLE_MATCH_MIN_SIMILARITY = 0.6;

const statusConfig = {
  active: { label: 'Aktif', color: 'bg-success/20 text-success' },
  inside: { label: 'İçeride', color: 'bg-accent/20 text-accent' },
  vip: { label: 'VIP', color: 'bg-warning/20 text-warning' },
};

const colorConfig = {
  mavi: { label: 'Mavi', bgClass: 'bg-blue-500/20', info: 'Standart Müşteri' },
  kirmizi: { label: 'Kırmızı', bgClass: 'bg-red-500/20', info: 'Dikkat!' },
  yesil: { label: 'Yeşil', bgClass: 'bg-green-500/20', info: 'Opsiyonel' },
  sari: { label: 'Sarı', bgClass: 'bg-yellow-500/20', info: 'Opsiyonel' },
  siyah: { label: 'Siyah', bgClass: 'bg-gray-500/20', info: 'Özel Müşteri Mobil Bildirim Gider' },
  beyaz: { label: 'Beyaz', bgClass: 'bg-slate-100/80', info: 'Çalışan' },
  gold: { label: 'Altın', bgClass: 'bg-yellow-600/20', info: 'Harcamaya göre VIP sistem tarafından otomatik atanır' },
};

const packageStatusConfig = {
  draft: { label: 'Taslak', color: 'bg-gray-500/20 text-gray-700' },
  offered: { label: 'Teklif Edildi', color: 'bg-blue-500/20 text-blue-700' },
  accepted: { label: 'Kabul Edildi', color: 'bg-green-500/20 text-green-700' },
  rejected: { label: 'Reddedildi', color: 'bg-red-500/20 text-red-700' },
  expired: { label: 'Süresi Doldu', color: 'bg-orange-500/20 text-orange-700' },
  converted: { label: 'Satışa Dönüştü', color: 'bg-purple-500/20 text-purple-700' },
  cancelled: { label: 'İptal Edildi', color: 'bg-gray-500/20 text-gray-700' },
};

interface AddCustomerNoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNoteAdd: (content: string) => void;
  isAdding: boolean;
}

const AddCustomerNoteModal = ({ 
  open, 
  onOpenChange, 
  onNoteAdd,
  isAdding 
}: AddCustomerNoteModalProps) => {
  const [content, setContent] = useState('');

  const handleSubmit = () => {
    if (content.trim()) {
      onNoteAdd(content.trim());
      setContent('');
    }
  };

  useEffect(() => {
    if (!open) {
      setContent('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Müşteriye Not Ekle</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Note Content */}
          <div className="space-y-2">
            <Label>Not İçeriği</Label>
            <Textarea
              placeholder="Müşteri notu yazın..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              className="resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              İptal
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={!content.trim() || isAdding}
              className="flex-1"
            >
              {isAdding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ekleniyor...
                </>
              ) : (
                'Not Ekle'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { companyId, loading: authLoading } = useAuth();
  const [showNewVisitModal, setShowNewVisitModal] = useState(false);
  const [showAddCalendarModal, setShowAddCalendarModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [customerNotes, setCustomerNotes] = useState<any[]>([]);
  const [visitFaces, setVisitFaces] = useState<any[]>([]);
  const [visitMediaLinks, setVisitMediaLinks] = useState<any[]>([]);
  const [visitFaceUrls, setVisitFaceUrls] = useState<Record<string, string>>({});
  const [timelineEvents, setTimelineEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendingKvkkSms, setSendingKvkkSms] = useState(false);
  const [kvkkRefreshToken, setKvkkRefreshToken] = useState(0);
  const [isUpdatingColor, setIsUpdatingColor] = useState(false);
  const [activeTab, setActiveTab] = useState('visits');
  const [possibleOldMatches, setPossibleOldMatches] = useState<PossibleOldMatch[]>([]);
  const [loadingPossibleOldMatches, setLoadingPossibleOldMatches] = useState(false);
  const [matchingVisitId, setMatchingVisitId] = useState<string | null>(null);
  const [possibleMatchImageUrls, setPossibleMatchImageUrls] = useState<Record<string, string>>({});
  
  // Calendar hook for creating events
  const { createEvent, isCreating } = useCalendar({ fetchEvents: false });
  
  // KVKK status hook
  const kvkkStatus = useKvkkStatus(customer?.phone || null, kvkkRefreshToken);
  const { toast } = useToast();
  const isConsentDeclined =
    kvkkStatus.type === 'declined' ||
    kvkkStatus.type === 'denied' ||
    kvkkStatus.type === 'withdrawn';
  const consentDeclinedMessage = kvkkStatus.type === 'withdrawn'
    ? 'Müşteri KVKK onayını geri çektiği için bu alan erişime kapatılmıştır. Sadece üst müşteri kartı ve Satışlar sekmesi görüntülenebilir.'
    : 'Müşteri KVKK onayını reddettiği için bu alan erişime kapatılmıştır. Sadece üst müşteri kartı ve Satışlar sekmesi görüntülenebilir.';

  // Debug: Log KVKK status changes
  useEffect(() => {
    console.log('📊 [KVKK Status] Status changed:', kvkkStatus);
  }, [kvkkStatus]);

  const getPhotoUrl = (photo: any) => {
    if (!photo.media_link) return null;
    const { data } = supabase.storage
      .from(photo.media_link.bucket)
      .getPublicUrl(photo.media_link.path);
    return data.publicUrl;
  };

  const getFaceSnapshotUrl = async (snapshotPath: string | null): Promise<string | null> => {
    if (!snapshotPath) return null;

    const attempts: Array<[string, string]> = [];
    const rawPath = snapshotPath.replace(/^\/+/, '');
    const strippedPath = rawPath
      .replace(/^faces\//, '')
      .replace(/^visit-faces\//, '')
      .replace(/^visit_faces\//, '');

    attempts.push(['faces', rawPath], ['faces', strippedPath], ['visit-faces', strippedPath], ['visit_faces', strippedPath]);

    for (const [bucket, path] of attempts) {
      try {
        const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
        if (!error && data?.signedUrl) return data.signedUrl;
      } catch {
        // try next candidate
      }
    }

    return null;
  };

  const loadPossibleOldMatches = async (targetCustomerId: string) => {
    if (!companyId) return;

    setLoadingPossibleOldMatches(true);
    try {
      const sevenDaysAgoIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const { data: oldUnknownVisits, error: visitsError } = await (supabase as any)
        .from('visits')
        .select('id, entered_at, created_at')
        .eq('company_id', companyId)
        .is('customer_id', null)
        .eq('status', 'PENDING_LABEL')
        .lt('entered_at', sevenDaysAgoIso)
        .order('entered_at', { ascending: false })
        .limit(200);

      if (visitsError) throw visitsError;

      const visitRows = (oldUnknownVisits || []) as Array<{ id: string; entered_at: string | null; created_at: string }>;
      if (visitRows.length === 0) {
        setPossibleOldMatches([]);
        return;
      }

      const visitAtById = new Map<string, string>();
      visitRows.forEach((row) => {
        visitAtById.set(row.id, row.entered_at || row.created_at);
      });

      const { data: visitFaceRows, error: facesError } = await (supabase as any)
        .from('visit_faces')
        .select('id, visit_id, snapshot_path, quality_score, created_at')
        .in('visit_id', visitRows.map((row) => row.id))
        .order('created_at', { ascending: false });

      if (facesError) throw facesError;

      const latestFaceByVisit = new Map<
        string,
        { id: string; visit_id: string; snapshot_path: string | null; quality_score: number | null }
      >();
      (visitFaceRows || []).forEach((row: any) => {
        if (!latestFaceByVisit.has(row.visit_id)) {
          latestFaceByVisit.set(row.visit_id, row);
        }
      });

      const facesToCheck = Array.from(latestFaceByVisit.values()).slice(0, 120);
      const foundMatches: PossibleOldMatch[] = [];

      const runWithConcurrency = async <T,>(items: T[], concurrency: number, worker: (item: T) => Promise<void>) => {
        let nextIndex = 0;
        const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
          while (nextIndex < items.length) {
            const current = items[nextIndex++];
            await worker(current);
          }
        });
        await Promise.all(runners);
      };

      await runWithConcurrency(facesToCheck, 8, async (face) => {
        const { data, error } = await supabase.rpc('find_visit_lookalikes' as any, {
          p_visit_face_id: face.id,
          p_limit: 10,
        });

        if (error) return;
        const candidates = (data || []) as Array<{ customer_id: string; similarity: number }>;
        const candidateForCustomer = candidates.find((candidate) => candidate.customer_id === targetCustomerId);
        if (!candidateForCustomer) return;
        const similarity = Math.max(0, Math.min(1, Number(candidateForCustomer.similarity) || 0));
        if (similarity < POSSIBLE_MATCH_MIN_SIMILARITY) return;

        foundMatches.push({
          visitId: face.visit_id,
          visitFaceId: face.id,
          visitAt: visitAtById.get(face.visit_id) || new Date().toISOString(),
          similarity,
          qualityScore: face.quality_score,
          snapshotPath: face.snapshot_path,
        });
      });

      const sorted = foundMatches
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 50);

      setPossibleOldMatches(sorted);

      const imagePairs = await Promise.all(
        sorted.map(async (item) => {
          const url = await getFaceSnapshotUrl(item.snapshotPath);
          return url ? ([item.visitFaceId, url] as const) : null;
        })
      );
      const nextImageUrls: Record<string, string> = {};
      imagePairs.forEach((pair) => {
        if (!pair) return;
        nextImageUrls[pair[0]] = pair[1];
      });
      setPossibleMatchImageUrls(nextImageUrls);
    } catch (error) {
      console.error('Error loading possible old matches:', error);
      toast({
        title: 'Hata',
        description: 'Olası eski eşleşmeler yüklenirken bir hata oluştu',
        variant: 'destructive',
      });
      setPossibleOldMatches([]);
    } finally {
      setLoadingPossibleOldMatches(false);
    }
  };

  const handleMatchOldVisitToCustomer = async (visitId: string) => {
    if (!customer) return;

    setMatchingVisitId(visitId);
    try {
      await (await import('@/lib/visit-service')).VisitService.linkVisitToCustomer(visitId, customer.id);
      toast({
        title: 'Başarılı',
        description: 'Eski ziyaret bu müşteriyle eşleştirildi',
      });
      await Promise.all([fetchCustomerData(), loadPossibleOldMatches(customer.id)]);
    } catch (error) {
      console.error('Error matching old visit to customer:', error);
      toast({
        title: 'Hata',
        description: 'Eski ziyaret eşleştirilirken bir hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setMatchingVisitId(null);
    }
  };

  const getTimelineIcon = (type: string) => {
    switch (type) {
      case 'visit':
        return UserCheck;
      case 'sale':
        return ShoppingBag;
      case 'package':
        return Package;
      case 'note':
        return FileText;
      default:
        return Clock;
    }
  };

  const getTimelineColor = (type: string) => {
    switch (type) {
      case 'visit':
        return 'bg-blue-500';
      case 'sale':
        return 'bg-green-500';
      case 'package':
        return 'bg-purple-500';
      case 'note':
        return 'bg-orange-500';
      default:
        return 'bg-gray-500';
    }
  };

  const createTimelineEvents = (visits: VisitRow[], sales: SaleRow[], packages: PackageRow[], notes: any[]) => {
    const events: any[] = [];

    // Add visits to timeline
    visits?.forEach(visit => {
      const visitDate = (visit as any).entered_at || (visit as any).started_at || visit.created_at;
      const statusLabel = String((visit as any).status || '').toUpperCase();
      const isLabeled = statusLabel === 'LABELED';
      events.push({
        id: `visit-${visit.id}`,
        date: visitDate,
        type: 'visit',
        title: isLabeled ? 'Ziyaret tamamlandı' : 'Ziyaret kaydedildi',
        description: `Durum: ${isLabeled ? 'Etiketlendi' : statusLabel === 'IGNORED' ? 'Yok sayıldı' : 'Beklemede'}`,
        data: visit
      });
    });

    // Add sales to timeline
    sales?.forEach(sale => {
      events.push({
        id: `sale-${sale.id}`,
        date: sale.sale_datetime,
        type: 'sale',
        title: 'Satış gerçekleşti',
        description: `₺${(sale.total_net || 0).toLocaleString('tr-TR')} • ${sale.note || 'Satış tamamlandı'}`,
        data: sale
      });
    });

    // Add packages to timeline
    packages?.forEach(pkg => {
      events.push({
        id: `package-${pkg.id}`,
        date: pkg.created_at,
        type: 'package',
        title: 'Paket oluşturuldu',
        description: `₺${(pkg.total_net || 0).toLocaleString('tr-TR')} • ${packageStatusConfig[pkg.status as keyof typeof packageStatusConfig]?.label || pkg.status}`,
        data: pkg
      });
    });

    // Add notes to timeline
    notes?.forEach(note => {
      events.push({
        id: `note-${note.id}`,
        date: note.created_at,
        type: 'note',
        title: 'Not eklendi',
        description: note.body || note.content,
        data: note
      });
    });

    // Sort by date (newest first)
    return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const determineStatus = (customerData: CustomerRow, isCurrentlyInside: boolean): 'active' | 'inside' | 'vip' => {
    if (isCurrentlyInside) return 'inside';
    if (customerData.tags_manual?.includes('vip')) return 'vip';
    return 'active';
  };

  /** 3 hours in seconds - system auto-closure; exclude from avg duration */
  const SYSTEM_CLOSURE_DURATION_SEC = 3 * 60 * 60; // 10800

  const calculateAvgDuration = (visits: VisitRow[], _customerData?: CustomerRow): string => {
    const durations: number[] = [];
    for (const visit of visits) {
      const v = visit as any;
      let sec: number | null = v.visit_duration_sec ?? v.duration_sec ?? null;
      if (sec == null && v.entered_at && v.exited_at) {
        const start = new Date(v.entered_at).getTime();
        const end = new Date(v.exited_at).getTime();
        if (!Number.isNaN(start) && !Number.isNaN(end) && end > start) {
          sec = Math.round((end - start) / 1000);
        }
      }
      if (sec != null && sec > 0 && sec !== SYSTEM_CLOSURE_DURATION_SEC) {
        durations.push(sec);
      }
    }
    if (durations.length === 0) return '—';
    const avg = Math.round(durations.reduce((a, b) => a + b, 0) / durations.length);
    return formatDuration(avg);
  };

  const calculateVisitPeriodSummary = (visitItems: Visit[]) => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

    let weekly = 0;
    let monthly = 0;
    let yearly = 0;

    for (const visit of visitItems) {
      if (!visit.visitedAt) continue;
      const visitDate = new Date(visit.visitedAt);
      if (Number.isNaN(visitDate.getTime())) continue;

      if (visitDate >= weekAgo) weekly += 1;
      if (visitDate >= monthAgo) monthly += 1;
      if (visitDate >= yearAgo) yearly += 1;
    }

    return { weekly, monthly, yearly };
  };

  const calculateVisitFrequencyFromVisits = (visitRows: VisitRow[]): string => {
    const visitDates = visitRows
      .map((visit) => {
        const visitAny = visit as any;
        const rawDate = visitAny.entered_at || visitAny.started_at || visit.created_at;
        if (!rawDate) return null;
        const parsed = new Date(rawDate);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      })
      .filter((date): date is Date => date !== null)
      .sort((a, b) => a.getTime() - b.getTime());

    const visitCount = visitDates.length;
    if (visitCount === 0) return 'Yeni müşteri';

    const now = new Date();
    const lastVisit = visitDates[visitCount - 1];

    if (visitCount === 1) {
      const daysSinceLast = Math.floor((now.getTime() - lastVisit.getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceLast <= 7) return 'Tek ziyaret (son 7 gün içinde)';
      if (daysSinceLast <= 30) return `Tek ziyaret (${daysSinceLast} gün önce)`;
      return `Tek ziyaret (${Math.floor(daysSinceLast / 30)} ay önce)`;
    }

    let totalDaysBetween = 0;
    for (let i = 1; i < visitDates.length; i++) {
      totalDaysBetween += (visitDates[i].getTime() - visitDates[i - 1].getTime()) / (1000 * 60 * 60 * 24);
    }
    const avgDaysBetween = Math.round(totalDaysBetween / (visitDates.length - 1));

    if (avgDaysBetween <= 10) return `Haftalık (ortalama ${avgDaysBetween} günde bir)`;
    if (avgDaysBetween <= 45) return `Aylık (ortalama ${avgDaysBetween} günde bir)`;
    if (avgDaysBetween <= 120) return `Üç ayda bir (ortalama ${avgDaysBetween} günde bir)`;
    return `Nadir (ortalama ${avgDaysBetween} günde bir)`;
  };

  const calculatePurchaseFrequency = (sales: Sale[]): {
    averageDaysBetween: number | null;
    frequencyText: string;
    lastPurchaseDate: string | null;
    totalPurchases: number;
    purchasesLast30Days: number;
    purchasesLast90Days: number;
  } => {
    if (sales.length === 0) {
      return {
        averageDaysBetween: null,
        frequencyText: 'Henüz satış yok',
        lastPurchaseDate: null,
        totalPurchases: 0,
        purchasesLast30Days: 0,
        purchasesLast90Days: 0,
      };
    }

    // Parse Turkish date format (DD.MM.YYYY) to Date object (fallback)
    const parseTurkishDate = (dateStr: string): Date => {
      const parts = dateStr.split('.');
      if (parts.length === 3) {
        // DD.MM.YYYY -> YYYY-MM-DD
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return new Date(dateStr);
    };

    // Get actual sale dates (not on-hold records)
    const actualSales = sales.filter(s => !s.isOnHold);
    if (actualSales.length === 0) {
      return {
        averageDaysBetween: null,
        frequencyText: 'Henüz tamamlanmış satış yok',
        lastPurchaseDate: null,
        totalPurchases: 0,
        purchasesLast30Days: 0,
        purchasesLast90Days: 0,
      };
    }

    // Sort dates (oldest first) - use saleDatetime if available for accuracy
    const sortedDates = actualSales
      .map(s => {
        // Prefer raw datetime if available, otherwise parse Turkish date
        if (s.saleDatetime) {
          return new Date(s.saleDatetime);
        }
        return parseTurkishDate(s.date);
      })
      .sort((a, b) => a.getTime() - b.getTime());

    const lastPurchaseDate = sortedDates[sortedDates.length - 1];
    const totalPurchases = actualSales.length;

    // Calculate days between purchases
    let totalDaysBetween = 0;
    let intervalsCount = 0;
    
    for (let i = 1; i < sortedDates.length; i++) {
      const daysDiff = (sortedDates[i].getTime() - sortedDates[i - 1].getTime()) / (1000 * 60 * 60 * 24);
      totalDaysBetween += daysDiff;
      intervalsCount++;
    }

    const averageDaysBetween = intervalsCount > 0 ? Math.round(totalDaysBetween / intervalsCount) : null;

    // Calculate purchases in last 30 and 90 days
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

    const purchasesLast30Days = sortedDates.filter(date => date >= thirtyDaysAgo).length;
    const purchasesLast90Days = sortedDates.filter(date => date >= ninetyDaysAgo).length;

    // Generate frequency text
    let frequencyText = 'Bilinmiyor';
    
    // Handle single purchase case
    if (totalPurchases === 1) {
      const daysSincePurchase = Math.floor((now.getTime() - lastPurchaseDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysSincePurchase <= 7) {
        frequencyText = 'Tek satış (son 7 gün içinde)';
      } else if (daysSincePurchase <= 30) {
        frequencyText = `Tek satış (${daysSincePurchase} gün önce)`;
      } else if (daysSincePurchase <= 90) {
        frequencyText = `Tek satış (${Math.floor(daysSincePurchase / 30)} ay önce)`;
      } else {
        frequencyText = `Tek satış (${Math.floor(daysSincePurchase / 30)}+ ay önce)`;
      }
    } else if (averageDaysBetween !== null && averageDaysBetween > 0) {
      // Multiple purchases - show frequency based on average interval
      if (averageDaysBetween <= 7) {
        frequencyText = `Haftalık (ortalama ${averageDaysBetween} günde bir)`;
      } else if (averageDaysBetween <= 14) {
        frequencyText = `İki haftada bir (ortalama ${averageDaysBetween} günde bir)`;
      } else if (averageDaysBetween <= 30) {
        frequencyText = `Aylık (ortalama ${averageDaysBetween} günde bir)`;
      } else if (averageDaysBetween <= 60) {
        frequencyText = `İki ayda bir (ortalama ${averageDaysBetween} günde bir)`;
      } else if (averageDaysBetween <= 90) {
        frequencyText = `Üç ayda bir (ortalama ${averageDaysBetween} günde bir)`;
      } else {
        frequencyText = `Nadir (ortalama ${averageDaysBetween} günde bir)`;
      }
    } else if (averageDaysBetween === 0) {
      // Multiple purchases on the same day(s)
      frequencyText = `${totalPurchases} satış (aynı gün içinde)`;
    } else {
      // Fallback - shouldn't happen, but handle it
      frequencyText = `${totalPurchases} satış`;
    }

    return {
      averageDaysBetween,
      frequencyText,
      lastPurchaseDate: lastPurchaseDate ? lastPurchaseDate.toLocaleDateString('tr-TR') : null,
      totalPurchases,
      purchasesLast30Days,
      purchasesLast90Days,
    };
  };

  const fetchCustomerData = async () => {
    console.log('🔍 fetchCustomerData called with:', { id, companyId });
    if (!id || !companyId) {
      console.log('❌ Missing id or companyId:', { id, companyId });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch customer data
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('company_id', companyId)
        .eq('id', id)
        .single();

      if (customerError) throw customerError;
      if (!customerData) throw new Error('Müşteri bulunamadı');

      // Check if currently inside
      const { data: activeVisit } = await supabase
        .from('visits')
        .select('id')
        .eq('company_id', companyId)
        .eq('customer_id', id)
        .eq('status', 'PENDING_LABEL')
        .single();

      const isInside = !!activeVisit;

      // Fetch visits (prefer entered_at for Facey records; fallback to started_at).
      let visitsData: VisitRow[] | null = null;
      {
        const baseQuery = supabase
          .from('visits')
          .select('*')
          .eq('company_id', companyId)
          .eq('customer_id', id);

        const byEnteredAt = await baseQuery.order('entered_at', { ascending: false });
        if (byEnteredAt.error && String(byEnteredAt.error.message || '').includes('entered_at')) {
          const byStartedAt = await supabase
            .from('visits')
            .select('*')
            .eq('company_id', companyId)
            .eq('customer_id', id)
            .order('started_at', { ascending: false });
          visitsData = byStartedAt.data as VisitRow[] | null;
        } else {
          visitsData = byEnteredAt.data as VisitRow[] | null;
        }
      }

      // Fetch converted onhold records to identify sales linked to onhold conversions
      const { data: convertedOnHolds } = await supabase
        .from('on_holds')
        .select('linked_sale_id')
        .eq('company_id', companyId)
        .eq('status', 'converted')
        .not('linked_sale_id', 'is', null);
      
      const onHoldLinkedSaleIds = new Set(
        (convertedOnHolds || [])
          .map((onHold: any) => onHold.linked_sale_id)
          .filter(Boolean)
      );

      // Fetch sales with items and payment info (including cancelled sales)
      const { data: salesDataRaw } = await supabase
        .from('sales')
        .select(`
          *,
          sale_items (
            id,
            quantity,
            products (
              name
            )
          ),
          sale_payments (
            id,
            amount,
            payment_type
          )
        `)
        .eq('company_id', companyId)
        .eq('customer_id', id)
        .order('sale_datetime', { ascending: false });

      // Filter out 0 TL sales that are linked to onhold conversions
      const salesData = (salesDataRaw || []).filter((sale) => {
        const isZeroAmount = Math.abs(sale.total_net || 0) < 0.01;
        const isLinkedToOnHold = onHoldLinkedSaleIds.has(sale.id);
        return !(isZeroAmount && isLinkedToOnHold);
      });

      // Fetch on-hold records for this customer
      const { data: onHoldData } = await supabase
        .from('on_holds')
        .select(`
          *,
          customer:customers(id, first_name, last_name, phone),
          product:products(name)
        `)
        .eq('company_id', companyId)
        .eq('customer_id', id)
        .eq('status', 'on_hold')
        .order('date_on_hold', { ascending: false });

      // Fetch linked sales for on-hold records that have linked_sale_id
      const linkedSaleIds = (onHoldData || [])
        .map((onHold: any) => onHold.linked_sale_id)
        .filter((id: string | null) => id !== null) as string[];
      
      let linkedSalesMap = new Map<string, any>();
      if (linkedSaleIds.length > 0) {
        const { data: linkedSalesData } = await supabase
          .from('sales')
          .select('id, total_net, total_paid, balance_due, sale_datetime')
          .in('id', linkedSaleIds);
        
        if (linkedSalesData) {
          linkedSalesData.forEach((sale: any) => {
            linkedSalesMap.set(sale.id, sale);
          });
        }
      }

      // Fetch packages with items
      console.log('📦 Fetching packages with params:', { companyId, customerId: id });
      
      // First, let's check ALL packages for this company to debug
      const { data: allCompanyPackages } = await supabase
        .from('packages')
        .select('id, customer_id, company_id, title, status')
        .eq('company_id', companyId);
      
      console.log('🏢 All packages for company:', allCompanyPackages);
      console.log('👤 Looking for customer_id:', id);
      
      const { data: packagesData, error: packagesError } = await supabase
        .from('packages')
        .select(`
          *,
          package_items (
            id,
            product_id,
            quantity,
            quoted_price_each,
            products (
              name
            )
          )
        `)
        .eq('company_id', companyId)
        .eq('customer_id', id)
        .order('created_at', { ascending: false });

      console.log('📦 Packages query result:', { packagesData, packagesError, count: packagesData?.length || 0 });
      
      if (packagesError) {
        console.error('❌ Packages query error:', packagesError);
      }

      // Fetch customer notes
      const { data: notesData } = await supabase
        .from('customer_notes')
        .select('*')
        .eq('company_id', companyId)
        .eq('customer_id', id)
        .order('created_at', { ascending: false });

      // Fetch visit faces (camera snapshots) for this customer's visits
      const visitIds = (visitsData || []).map((v: any) => v.id).filter(Boolean);
      let facesData: any[] = [];
      let mediaLinksData: any[] = [];
      if (visitIds.length > 0) {
        const { data: fData } = await supabase
          .from('visit_faces')
          .select('id, visit_id, snapshot_path, quality_score, blur_score, created_at, camera_id')
          .in('visit_id', visitIds)
          .order('created_at', { ascending: false });
        facesData = fData || [];

        const { data: mData } = await supabase
          .from('media_links')
          .select('id, owner_id, bucket, path, mime, created_at')
          .eq('owner_table', 'visits')
          .in('owner_id', visitIds)
          .order('created_at', { ascending: false });
        mediaLinksData = mData || [];
      }

      // Calculate stats (include on-hold sales in total spent)
      const regularSalesTotal = salesData?.reduce((sum, sale) => sum + (sale.total_net || 0), 0) || 0;
      const onHoldTotal = onHoldData?.reduce((sum, onHold) => sum + (parseFloat(onHold.hold_price || 0) * (onHold.hold_quantity || 1)), 0) || 0;
      const totalSpent = regularSalesTotal + onHoldTotal;
      const avgDuration = calculateAvgDuration(visitsData || [], customerData);
      
      // Determine visit stats directly from visits table data
      const visitCount = (visitsData || []).length;
      const frequency = calculateVisitFrequencyFromVisits(visitsData || []);

      const name = `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim() || 'İsimsiz Müşteri';

      const customerDetail: CustomerDetail = {
        id: customerData.id,
        name,
        email: customerData.email || '',
        phone: customerData.phone || '',
        status: determineStatus(customerData, isInside),
        colorTag: customerData.color,
        consent: true, // Assume consent if customer exists
        stats: {
          totalVisits: visitCount,
          avgDuration,
          frequency,
          totalSpent: `₺${totalSpent.toLocaleString('tr-TR')}`
        },
        customer_data: customerData
      };

      // Process visits
      const processedVisits: Visit[] = (visitsData || []).map((visit: any) => {
        const visitTimestamp = visit.entered_at || visit.started_at || visit.created_at;
        const statusLabel = String(visit.status || '').toUpperCase();
        return {
          id: visit.id,
          date: visitTimestamp
            ? new Date(visitTimestamp).toLocaleString('tr-TR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })
            : 'Bilinmiyor',
          visitedAt: visitTimestamp || null,
          location: visit.camera_id || 'Mağaza',
          duration: statusLabel === 'LABELED' ? 'Etiketlendi' : statusLabel === 'IGNORED' ? 'Yok sayıldı' : 'Beklemede',
          notes: visit.external_id ? `Facey kayıt: ${visit.external_id.slice(0, 8)}` : undefined,
        };
      });

      const sortedVisits = [...processedVisits].sort((a, b) => {
        const dateA = a.visitedAt ? new Date(a.visitedAt).getTime() : 0;
        const dateB = b.visitedAt ? new Date(b.visitedAt).getTime() : 0;
        return dateB - dateA;
      });

      // Helper function to determine payment status
      const determinePaymentStatus = (sale: any): 'paid' | 'partial' | 'pending' | 'overdue' => {
        if (sale.total_paid >= sale.total_net) return 'paid';
        if (sale.total_paid > 0) return 'partial';
        if (sale.balance_due > 0) {
          const daysSinceSale = (new Date().getTime() - new Date(sale.sale_datetime).getTime()) / (1000 * 60 * 60 * 24);
          return daysSinceSale > 30 ? 'overdue' : 'pending';
        }
        return 'pending';
      };

      // Process regular sales
      const processedSales: Sale[] = (salesData || []).map(sale => {
        const productNames = (sale.sale_items || [])
          .map((item: any) => item.products?.name)
          .filter((name: string) => name);
        // Show all products from the same sale
        
        const itemCount = sale.sale_items?.length || 0;
        const paymentStatus = determinePaymentStatus(sale);
        
        return {
          id: sale.id,
          date: new Date(sale.sale_datetime).toLocaleDateString('tr-TR'),
          saleDatetime: sale.sale_datetime, // Store raw datetime for accurate calculations
          total: `₺${(sale.total_net || 0).toLocaleString('tr-TR')}`,
          items: sale.note || 'Satış detayları',
          productNames,
          paymentStatus,
          totalPaid: sale.total_paid || 0,
          balanceDue: sale.balance_due || 0,
          itemCount,
          isOnHold: false,
          actualSaleId: sale.id,
          status: sale.status
        };
      });

      // Process on-hold sales
      const processedOnHoldSales: Sale[] = (onHoldData || []).map(onHold => {
        const productName = onHold.product?.name || 'Bilinmeyen Ürün';
        
        // If there's a linked sale, use its data from sales table
        const linkedSale = onHold.linked_sale_id ? linkedSalesMap.get(onHold.linked_sale_id) : null;
        let totalAmount: number;
        let totalPaid: number;
        let balanceDue: number;
        let saleDate: string;
        let saleDatetime: string;
        let paymentStatus: 'paid' | 'partial' | 'pending' | 'overdue';
        
        if (linkedSale) {
          // Use data from sales table
          totalAmount = linkedSale.total_net || 0;
          totalPaid = linkedSale.total_paid || 0;
          balanceDue = linkedSale.balance_due || 0;
          saleDate = new Date(linkedSale.sale_datetime).toLocaleDateString('tr-TR');
          saleDatetime = linkedSale.sale_datetime;
          paymentStatus = determinePaymentStatus(linkedSale);
        } else {
          // Fallback to on_hold table data
          totalAmount = parseFloat(onHold.hold_price || 0) * (onHold.hold_quantity || 1);
          totalPaid = 0;
          balanceDue = totalAmount;
          saleDate = new Date(onHold.date_on_hold).toLocaleDateString('tr-TR');
          saleDatetime = onHold.date_on_hold;
          paymentStatus = 'pending';
        }
        
        return {
          id: onHold.linked_sale_id || `onhold_${onHold.id}`,
          date: saleDate,
          saleDatetime: saleDatetime,
          total: `₺${totalAmount.toLocaleString('tr-TR')}`,
          items: onHold.note || 'Beklemedeki satış',
          productNames: [productName],
          paymentStatus,
          totalPaid,
          balanceDue,
          itemCount: 1,
          isOnHold: true,
          actualSaleId: onHold.linked_sale_id || onHold.id,
          linkedSaleId: onHold.linked_sale_id
        };
      });

      // Merge regular sales and on-hold sales
      const allSales = [...processedSales, ...processedOnHoldSales].sort((a, b) => {
        // Sort by date descending (newest first)
        // Parse Turkish date format (DD.MM.YYYY) to Date object
        const parseTurkishDate = (dateStr: string): Date => {
          const parts = dateStr.split('.');
          if (parts.length === 3) {
            // DD.MM.YYYY -> YYYY-MM-DD
            return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          }
          return new Date(dateStr);
        };
        const dateA = parseTurkishDate(a.date).getTime();
        const dateB = parseTurkishDate(b.date).getTime();
        return dateB - dateA;
      });

      // Process packages
      console.log('🔄 Processing packages data:', packagesData);
      const processedPackages: Package[] = (packagesData || []).map(pkg => {
        console.log('📋 Processing package:', {
          id: pkg.id,
          title: pkg.title,
          status: pkg.status,
          total_net: pkg.total_net,
          package_items_count: pkg.package_items?.length || 0
        });
        
        return {
          id: pkg.id,
          title: pkg.title || `Paket #${pkg.id.slice(-8)}`,
          status: pkg.status,
          total: `₺${(pkg.total_net || 0).toLocaleString('tr-TR')}`,
          created_at: pkg.created_at,
          items: (pkg.package_items || []).map((item: any) => ({
            id: item.id,
            name: item.products?.name || 'Ürün',
            quantity: item.quantity,
            unit_price: item.quoted_price_each || 0
          }))
        };
      });

      console.log('✅ Processed packages:', processedPackages);

      // Create timeline events from all data
      const timeline = createTimelineEvents(visitsData || [], salesData || [], packagesData || [], notesData || []);

      console.log('🎯 Setting state with:', {
        customer: customerDetail.name,
        visits_count: processedVisits.length,
        sales_count: processedSales.length,
        packages_count: processedPackages.length,
        notes_count: notesData?.length || 0,
        faces_count: facesData.length,
        media_links_count: mediaLinksData.length,
        timeline_count: timeline.length
      });

      setCustomer(customerDetail);
      setVisits(sortedVisits);
      setSales(allSales);
      setPackages(processedPackages);
      setCustomerNotes(notesData || []);
      setVisitFaces(facesData);
      setVisitMediaLinks(mediaLinksData);
      setTimelineEvents(timeline);

      console.log('✅ State set successfully');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Müşteri bilgileri yüklenirken hata oluştu');
      console.error('Error fetching customer:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔄 useEffect triggered with:', { id, companyId, authLoading });
    if (companyId && !authLoading) {
      console.log('✅ Conditions met, calling fetchCustomerData');
      void fetchCustomerData();
    } else {
      console.log('⏳ Waiting for companyId or authLoading to complete');
    }
  }, [id, companyId, authLoading]);

  useEffect(() => {
    if (activeTab === 'possible' && customer?.id) {
      void loadPossibleOldMatches(customer.id);
    }
  }, [activeTab, customer?.id]);

  // Load signed URLs for visit_faces snapshots
  useEffect(() => {
    if (visitFaces.length === 0) return;
    let cancelled = false;
    const load = async () => {
      const pairs = await Promise.all(
        visitFaces.map(async (face) => {
          const url = await getFaceSnapshotUrl(face.snapshot_path);
          return [face.id, url] as [string, string | null];
        })
      );
      if (!cancelled) {
        const map: Record<string, string> = {};
        pairs.forEach(([id, url]) => { if (url) map[id] = url; });
        setVisitFaceUrls(map);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [visitFaces]);

  useEffect(() => {
    if (isConsentDeclined && activeTab !== 'sales') {
      setActiveTab('sales');
    }
  }, [isConsentDeclined, activeTab]);

  useEffect(() => {
    if (!isConsentDeclined) return;

    const interval = window.setInterval(() => {
      setKvkkRefreshToken((prev) => prev + 1);
    }, 15000);

    return () => window.clearInterval(interval);
  }, [isConsentDeclined]);

  const handleVisitStarted = (visitData: any) => {
    // In real app, save visit and navigate to visit detail
    console.log('Visit started:', visitData);
    navigate(`/visits/${visitData.id || 'new'}`);
  };

  const handleViewPackage = (packageId: string) => {
    navigate(`/packages/${packageId}`);
  };


  const handleDeletePackage = async (packageId: string) => {
    if (!confirm('Bu paketi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('packages')
        .delete()
        .eq('company_id', companyId)
        .eq('id', packageId)
        .eq('status', 'draft'); // Only allow deleting draft packages

      if (error) throw error;

      // Refresh data
      await fetchCustomerData();
      console.log('Package deleted successfully');
    } catch (error) {
      console.error('Error deleting package:', error);
      // You might want to show an error toast here
    }
  };

  const handleAddCalendarEvent = async (eventData: any) => {
    try {
      await createEvent(eventData);
      setShowAddCalendarModal(false);
      console.log('Calendar event created successfully');
    } catch (error) {
      console.error('Error creating calendar event:', error);
    }
  };

  const handleAddCustomerNote = async (content: string) => {
    if (!id || !companyId || !content.trim()) {
      toast({
        title: 'Hata',
        description: 'Not içeriği boş olamaz',
        variant: 'destructive',
      });
      return;
    }

    setIsAddingNote(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id || null;

      const { error } = await supabase
        .from('customer_notes')
        .insert({
          company_id: companyId,
          customer_id: id,
          body: content.trim(),
          author_user_id: userId,
        });

      if (error) throw error;

      toast({
        title: 'Başarılı',
        description: 'Not başarıyla eklendi',
      });

      // Refresh customer data to show the new note
      await fetchCustomerData();
      setShowAddNoteModal(false);
    } catch (error) {
      console.error('Error adding customer note:', error);
      toast({
        title: 'Hata',
        description: 'Not eklenirken bir hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setIsAddingNote(false);
    }
  };

  const sendKvkkSms = async () => {
    console.log('📱 [KVKK SMS] ========================================');
    console.log('📱 [KVKK SMS] Starting sendKvkkSms function');
    console.log('📱 [KVKK SMS] companyId:', companyId);
    console.log('📱 [KVKK SMS] customer:', customer);
    console.log('📱 [KVKK SMS] customer.phone:', customer?.phone);
    console.log('📱 [KVKK SMS] supabase client:', supabase ? 'exists' : 'missing');
    console.log('📱 [KVKK SMS] supabase.functions:', supabase?.functions ? 'exists' : 'missing');
    console.log('📱 [KVKK SMS] supabase.functions.invoke:', typeof supabase?.functions?.invoke);

    if (!companyId || !customer?.phone) {
      console.error('❌ [KVKK SMS] Missing required data:', {
        hasCompanyId: !!companyId,
        hasPhone: !!customer?.phone,
      });
      toast({
        title: 'Hata',
        description: 'Şirket ID veya telefon numarası bulunamadı',
        variant: 'destructive',
      });
      return;
    }

    const requestBody = {
      companyId,
      phone: customer.phone,
    };

    console.log('📱 [KVKK SMS] Request body:', JSON.stringify(requestBody, null, 2));
    console.log('📱 [KVKK SMS] Calling supabase.functions.invoke with function name: kvkk-send-sms');

    setSendingKvkkSms(true);
    
    try {
      console.log('📱 [KVKK SMS] Making function call...');
      const { data, error } = await supabase.functions.invoke('kvkk-send-sms', {
        body: requestBody,
      });

      console.log('📱 [KVKK SMS] Function call completed');
      console.log('📱 [KVKK SMS] Response data:', data);
      console.log('📱 [KVKK SMS] Response error:', error);

      if (error) {
        console.error('❌ [KVKK SMS] Error received:', error);
        console.error('❌ [KVKK SMS] Error message:', error.message);
        console.error('❌ [KVKK SMS] Error context:', error.context);
        console.error('❌ [KVKK SMS] Full error object:', JSON.stringify(error, null, 2));
        
        const errorMessage = error.message || 'KVKK SMS gönderilemedi. Lütfen tekrar deneyin.';
        toast({
          title: 'Hata',
          description: errorMessage,
          variant: 'destructive',
        });
        return;
      }

      console.log('✅ [KVKK SMS] Success! Data received:', data);
      toast({
        title: 'Başarılı',
        description: 'KVKK SMS başarıyla gönderildi',
      });
      setKvkkRefreshToken((prev) => prev + 1);
    } catch (err) {
      console.error('❌ [KVKK SMS] Exception caught:', err);
      console.error('❌ [KVKK SMS] Exception type:', typeof err);
      console.error('❌ [KVKK SMS] Exception details:', err instanceof Error ? {
        name: err.name,
        message: err.message,
        stack: err.stack,
      } : err);
      console.error('❌ [KVKK SMS] Full exception:', JSON.stringify(err, null, 2));
      
      toast({
        title: 'Hata',
        description: 'KVKK SMS gönderilirken bir hata oluştu',
        variant: 'destructive',
      });
    } finally {
      console.log('📱 [KVKK SMS] Cleaning up, setting sendingKvkkSms to false');
      console.log('📱 [KVKK SMS] ========================================');
      setSendingKvkkSms(false);
    }
  };

  const handleQuickColorUpdate = async (nextColor: CustomerRow['color']) => {
    if (!customer || !companyId) return;
    if (customer.colorTag === nextColor) return;

    setIsUpdatingColor(true);
    try {
      const { error } = await supabase
        .from('customers')
        .update({ color: nextColor })
        .eq('company_id', companyId)
        .eq('id', customer.id);

      if (error) throw error;

      setCustomer((prev) =>
        prev
          ? {
              ...prev,
              colorTag: nextColor,
              customer_data: {
                ...prev.customer_data,
                color: nextColor,
              },
            }
          : prev
      );

      toast({
        title: 'Renk güncellendi',
        description: 'Müşteri renk etiketi kaydedildi.',
      });
    } catch (error) {
      console.error('Error updating customer color:', error);
      toast({
        title: 'Hata',
        description: 'Renk etiketi güncellenirken bir hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingColor(false);
    }
  };

  if (loading || authLoading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <Loader2 className="w-8 h-8 animate-spin" />
          <h2 className="text-xl font-semibold">Müşteri bilgileri yükleniyor...</h2>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <h2 className="text-xl font-semibold text-red-600">Hata oluştu</h2>
          <p className="text-muted-foreground">{error}</p>
          <div className="flex gap-2">
            <Button onClick={fetchCustomerData} variant="outline">
              Tekrar Dene
            </Button>
            <Button onClick={() => navigate('/customers')}>
              Müşteri Listesine Dön
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!customer) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <h2 className="text-xl font-semibold">Müşteri bulunamadı</h2>
          <Button onClick={() => navigate('/customers')}>
            Müşteri Listesine Dön
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/customers')}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Müşteri Listesi
        </Button>

        {/* Header */}
        <div className={`glass-panel p-6 ${colorConfig[customer.colorTag]?.bgClass || ''}`}>
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex items-start gap-4">
              <Avatar className="w-20 h-20 border-2 border-white/20">
                <AvatarImage src={customer.avatar} alt={customer.name} />
                <AvatarFallback className="bg-accent/20 text-accent text-xl font-semibold">
                  {customer.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              
              <div className="space-y-2 overflow-visible">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold">{customer.name}</h1>
                  {customer.consent && (
                    <Shield className="w-5 h-5 text-success" />
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2 overflow-visible">
                  <Badge 
                    variant="secondary" 
                    className={statusConfig[customer.status].color}
                  >
                    {statusConfig[customer.status].label}
                  </Badge>
                  <Badge variant="outline" className={`${colorConfig[customer.colorTag]?.bgClass || 'bg-gray-500/20'} text-gray-700`}>
                    {colorConfig[customer.colorTag]?.label || customer.colorTag}
                  </Badge>
                  <CustomerTagList
                    manualTags={customer.customer_data.tags_manual || []}
                    autoTags={customer.customer_data.tags_auto || []}
                    showIcons={true}
                    showTooltips={true}
                    size="sm"
                    maxDisplay={8}
                  />
                </div>
                
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>{customer.email}</p>
                  <p>{customer.phone}</p>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <span className="text-xs text-muted-foreground">Hızlı Renk:</span>
                  <Select
                    value={(customer.colorTag || 'mavi') as string}
                    onValueChange={(value) => void handleQuickColorUpdate(value as CustomerRow['color'])}
                    disabled={isUpdatingColor}
                  >
                    <SelectTrigger className="min-h-8 h-auto w-full sm:w-[440px]">
                      <SelectValue placeholder="Renk seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(colorConfig).map(([value, config]) => (
                        <SelectItem key={value} value={value}>
                          <div className="flex items-start gap-2 max-w-[380px] whitespace-normal">
                            <div className={`w-3 h-3 rounded-full mt-1 shrink-0 ${config.bgClass}`} />
                            <span className="font-medium shrink-0">{config.label}</span>
                            <span className="text-xs text-muted-foreground">- {config.info}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isUpdatingColor && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 md:ml-auto">
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => setShowEditCustomerModal(true)}
              >
                <Edit className="w-4 h-4" />
                Müşteri Düzenle
              </Button>
              <Button 
                className="gap-2"
                onClick={() => setShowNewVisitModal(true)}
              >
                <Plus className="w-4 h-4" />
                Ziyaret Kaydı Oluştur
              </Button>
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => navigate(`/packages/new?customerId=${customer.id}`)}
              >
                <Package className="w-4 h-4" />
                Paket Oluştur
              </Button>
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => setShowAddCalendarModal(true)}
              >
                <Calendar className="w-4 h-4" />
                Takvime Ekle
              </Button>
            </div>
          </div>
        </div>

        {/* KVKK Section */}
        <KvkkConsentSection
          phone={customer.phone || null}
          customerId={customer.id || null}
          onSendSms={customer.phone ? sendKvkkSms : undefined}
          sendingSms={sendingKvkkSms}
          consentDeclinedMessage={isConsentDeclined ? consentDeclinedMessage : undefined}
          onRefreshStatus={isConsentDeclined ? () => setKvkkRefreshToken((prev) => prev + 1) : undefined}
          statusLoading={kvkkStatus.loading}
        />

        {/* Overview Stats */}
        {isConsentDeclined ? (
          <div className="relative rounded-xl overflow-hidden border border-red-200/80">
            <div className="pointer-events-none select-none blur-[3px] opacity-30">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-1">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Toplam Ziyaret</p>
                        <p className="text-xl font-bold">{customer.stats.totalVisits}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                        <Star className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Ziyaret sıklığı</p>
                        <p className="text-xl font-bold">{customer.stats.frequency}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                        <ShoppingBag className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Toplam Harcama</p>
                        <p className="text-xl font-bold">{customer.stats.totalSpent}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Toplam Ziyaret</p>
                    <p className="text-xl font-bold">{customer.stats.totalVisits}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Star className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Ziyaret sıklığı</p>
                    <p className="text-xl font-bold">{customer.stats.frequency}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Toplam Harcama</p>
                    <p className="text-xl font-bold">{customer.stats.totalSpent}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(nextTab) => {
            if (isConsentDeclined && nextTab !== 'sales') return;
            setActiveTab(nextTab);
          }}
          className="space-y-4"
        >
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger
              value="visits"
              disabled={isConsentDeclined}
              className={isConsentDeclined ? 'opacity-50 cursor-not-allowed' : ''}
            >
              Ziyaretler
            </TabsTrigger>
            <TabsTrigger
              value="packages"
              disabled={isConsentDeclined}
              className={isConsentDeclined ? 'opacity-50 cursor-not-allowed' : ''}
            >
              Paketler
            </TabsTrigger>
            <TabsTrigger value="sales">Satışlar</TabsTrigger>
            <TabsTrigger
              value="possible"
              disabled={isConsentDeclined}
              className={isConsentDeclined ? 'opacity-50 cursor-not-allowed' : ''}
            >
              Olabilir mi?
            </TabsTrigger>
            <TabsTrigger
              value="media"
              disabled={isConsentDeclined}
              className={isConsentDeclined ? 'opacity-50 cursor-not-allowed' : ''}
            >
              Medya
            </TabsTrigger>
            <TabsTrigger
              value="fields"
              disabled={isConsentDeclined}
              className={isConsentDeclined ? 'opacity-50 cursor-not-allowed' : ''}
            >
              Bilgiler & Notlar
            </TabsTrigger>
            <TabsTrigger
              value="timeline"
              disabled={isConsentDeclined}
              className={isConsentDeclined ? 'opacity-50 cursor-not-allowed' : ''}
            >
              Zaman Çizelgesi
            </TabsTrigger>
          </TabsList>

          {/* Visits Tab */}
          <TabsContent value="visits">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Ziyaretler</CardTitle>
                  <Button 
                    className="gap-2"
                    onClick={() => setShowNewVisitModal(true)}
                  >
                    <Plus className="w-4 h-4" />
                    Yeni Ziyaret Kaydı
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const summary = calculateVisitPeriodSummary(visits);
                  return (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                        <div className="rounded-lg border p-3 bg-card/50">
                          <p className="text-xs text-muted-foreground">Son 7 Gün</p>
                          <p className="text-xl font-semibold">{summary.weekly}</p>
                          <p className="text-xs text-muted-foreground">ziyaret</p>
                        </div>
                        <div className="rounded-lg border p-3 bg-card/50">
                          <p className="text-xs text-muted-foreground">Son 30 Gün</p>
                          <p className="text-xl font-semibold">{summary.monthly}</p>
                          <p className="text-xs text-muted-foreground">ziyaret</p>
                        </div>
                        <div className="rounded-lg border p-3 bg-card/50">
                          <p className="text-xs text-muted-foreground">Son 1 Yıl</p>
                          <p className="text-xl font-semibold">{summary.yearly}</p>
                          <p className="text-xs text-muted-foreground">ziyaret</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {visits.length > 0 ? (
                          visits.map((visit) => (
                            <div key={visit.id} className="p-4 rounded-lg border bg-card/50">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h4 className="font-semibold">{visit.date}</h4>
                                  <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-3 h-3" />
                                      {visit.location}
                                    </span>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/visits/${visit.id}`)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </div>

                              {visit.products && visit.products.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-3">
                                  {visit.products.map((product, index) => (
                                    <Badge key={index} variant="secondary" className="text-xs">
                                      {product}
                                    </Badge>
                                  ))}
                                </div>
                              )}

                              {visit.notes && (
                                <p className="text-sm text-muted-foreground">{visit.notes}</p>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8">
                            <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                            <p className="text-muted-foreground">Bu müşteri için henüz ziyaret kaydı yok</p>
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Possible Old Matches Tab */}
          <TabsContent value="possible">
            <Card>
              <CardHeader>
                <CardTitle>Olabilir mi?</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingPossibleOldMatches ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-10 h-10 text-muted-foreground mx-auto mb-3 animate-spin" />
                    <p className="text-muted-foreground">Olası eski eşleşmeler aranıyor...</p>
                  </div>
                ) : possibleOldMatches.length > 0 ? (
                  <div className="space-y-4">
                    {possibleOldMatches.map((match) => (
                      <div key={match.visitFaceId} className="p-4 rounded-lg border bg-card/50">
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="w-32 h-32 rounded-lg overflow-hidden bg-muted shrink-0">
                            {possibleMatchImageUrls[match.visitFaceId] ? (
                              <img
                                src={possibleMatchImageUrls[match.visitFaceId]}
                                alt="Olası geçmiş ziyaret yüzü"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <User className="w-6 h-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>

                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="bg-blue-500/20 text-blue-700">
                                Benzerlik %{(match.similarity * 100).toFixed(0)}
                              </Badge>
                              {typeof match.qualityScore === 'number' && (
                                <Badge variant="outline">Kalite: {match.qualityScore.toFixed(2)}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Ziyaret zamanı: {new Date(match.visitAt).toLocaleString('tr-TR')}
                            </p>
                            <p className="text-xs text-muted-foreground">Ziyaret ID: {match.visitId}</p>
                          </div>

                          <div className="sm:self-start">
                            <Button
                              onClick={() => handleMatchOldVisitToCustomer(match.visitId)}
                              disabled={matchingVisitId === match.visitId}
                              className="gap-2"
                            >
                              {matchingVisitId === match.visitId ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Eşleştiriliyor...
                                </>
                              ) : (
                                <>
                                  <UserCheck className="w-4 h-4" />
                                  Bu müşteriyle eşleştir
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      Bu müşteri için 7+ gün eski kayıtlar içinde güçlü bir olası eşleşme bulunamadı.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Packages Tab */}
          <TabsContent value="packages">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Paketler</CardTitle>
                  <Button 
                    className="gap-2"
                    onClick={() => navigate(`/packages/new?customerId=${customer.id}`)}
                  >
                    <Plus className="w-4 h-4" />
                    Yeni Paket
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  console.log('🎨 Rendering packages tab with packages:', packages);
                  console.log('📊 Packages count:', packages.length);
                  return null;
                })()}
                {packages.length > 0 ? (
                  <div className="space-y-4">
                    {packages.map((pkg) => (
                      <div key={pkg.id} className="p-4 rounded-lg border bg-card/50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold">{pkg.title}</h4>
                              <Badge 
                                variant="outline" 
                                className={packageStatusConfig[pkg.status as keyof typeof packageStatusConfig]?.color || 'bg-gray-500/20 text-gray-700'}
                              >
                                {packageStatusConfig[pkg.status as keyof typeof packageStatusConfig]?.label || pkg.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 mb-2">
                              <p className="text-lg font-bold text-accent">{pkg.total}</p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(pkg.created_at).toLocaleDateString('tr-TR')}
                              </p>
                            </div>
                            
                            {/* Package Items */}
                            {pkg.items.length > 0 ? (
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-muted-foreground">Paket İçeriği:</p>
                                <div className="space-y-1">
                                  {pkg.items.map((item, index) => (
                                    <div key={item.id || index} className="flex items-center gap-2 text-sm bg-muted/50 p-2 rounded">
                                      <span className="font-medium">{item.name}</span>
                                      <span className="text-muted-foreground">
                                        {item.quantity} x ₺{item.unit_price.toLocaleString('tr-TR')}
                                      </span>
                                      <span className="ml-auto font-medium">
                                        ₺{(item.quantity * item.unit_price).toLocaleString('tr-TR')}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">Paket içeriği belirlenmemiş</p>
                            )}
                          </div>
                          <div className="flex gap-1 ml-4">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleViewPackage(pkg.id)}
                              title="Paketi görüntüle"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {pkg.status === 'draft' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeletePackage(pkg.id)}
                                title="Paketi sil"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">Henüz paket oluşturulmamış</p>
                    <Button 
                      className="gap-2"
                      onClick={() => navigate(`/packages/new?customerId=${customer.id}`)}
                    >
                      <Plus className="w-4 h-4" />
                      İlk paketi oluştur
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sales Tab */}
          <TabsContent value="sales">
            <Card>
              <CardHeader>
                <CardTitle>Satışlar</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Purchase Frequency Analysis */}
                {(() => {
                  const frequencyData = calculatePurchaseFrequency(sales);
                  
                  // Calculate days since last purchase
                  let daysSinceLastPurchase: number | null = null;
                  const actualSales = sales.filter(s => !s.isOnHold);
                  if (actualSales.length > 0) {
                    // Find the most recent sale using saleDatetime if available
                    const lastSale = actualSales.reduce((latest, current) => {
                      const latestDate = latest.saleDatetime ? new Date(latest.saleDatetime) : 
                        (() => {
                          const parts = latest.date.split('.');
                          return parts.length === 3 ? new Date(`${parts[2]}-${parts[1]}-${parts[0]}`) : new Date(latest.date);
                        })();
                      const currentDate = current.saleDatetime ? new Date(current.saleDatetime) :
                        (() => {
                          const parts = current.date.split('.');
                          return parts.length === 3 ? new Date(`${parts[2]}-${parts[1]}-${parts[0]}`) : new Date(current.date);
                        })();
                      return currentDate > latestDate ? current : latest;
                    });
                    
                    const lastPurchaseDate = lastSale.saleDatetime ? new Date(lastSale.saleDatetime) :
                      (() => {
                        const parts = lastSale.date.split('.');
                        return parts.length === 3 ? new Date(`${parts[2]}-${parts[1]}-${parts[0]}`) : new Date(lastSale.date);
                      })();
                    
                    daysSinceLastPurchase = Math.floor((new Date().getTime() - lastPurchaseDate.getTime()) / (1000 * 60 * 60 * 24));
                  }

                  return (
                    <div className="mb-6 p-4 rounded-lg border bg-gradient-to-r from-blue-50/50 to-purple-50/50 dark:from-blue-950/20 dark:to-purple-950/20">
                      <div className="flex items-center gap-2 mb-4">
                        <ShoppingBag className="w-5 h-5 text-accent" />
                        <h3 className="text-lg font-semibold">Satın Alma Sıklığı</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Satın Alma Sıklığı</p>
                          <p className="text-base font-semibold text-foreground">{frequencyData.frequencyText}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Son Satın Alma</p>
                          <p className="text-base font-semibold text-foreground">
                            {frequencyData.lastPurchaseDate || 'Yok'}
                            {daysSinceLastPurchase !== null && (
                              <span className="text-xs text-muted-foreground ml-2">
                                ({daysSinceLastPurchase} gün önce)
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Son 30 Gün</p>
                          <p className="text-base font-semibold text-foreground">
                            {frequencyData.purchasesLast30Days} satış
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Son 90 Gün</p>
                          <p className="text-base font-semibold text-foreground">
                            {frequencyData.purchasesLast90Days} satış
                          </p>
                        </div>
                      </div>
                      {frequencyData.averageDaysBetween !== null && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                              Toplam <span className="font-semibold text-foreground">{frequencyData.totalPurchases} satış</span>
                              {frequencyData.averageDaysBetween > 0 && (
                                <> • Ortalama <span className="font-semibold text-foreground">{frequencyData.averageDaysBetween} günde bir</span> alışveriş yapıyor</>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                
                <div className="space-y-4">
                  {sales.map((sale) => {
                    const paymentStatusConfig = {
                      paid: { label: 'Ödendi', color: 'bg-green-500/20 text-green-700' },
                      partial: { label: 'Kısmi Ödeme', color: 'bg-yellow-500/20 text-yellow-700' },
                      pending: { label: 'Beklemede', color: 'bg-orange-500/20 text-orange-700' },
                      overdue: { label: 'Gecikmiş', color: 'bg-red-500/20 text-red-700' },
                    };
                    const statusConfig = paymentStatusConfig[sale.paymentStatus] || paymentStatusConfig.pending;
                    
                    const isCancelled = sale.status === 'cancelled';
                    
                    return (
                      <div 
                        key={sale.id} 
                        className={`p-4 rounded-lg border transition-colors ${
                          sale.isOnHold 
                            ? 'bg-orange-50/50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800 hover:bg-orange-100/50 dark:hover:bg-orange-950/30' 
                            : isCancelled
                            ? 'bg-destructive/5 dark:bg-destructive/10 border-destructive/20 dark:border-destructive/30'
                            : 'bg-card/50 hover:bg-card/70'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className={`font-semibold ${isCancelled ? 'text-destructive/70' : ''}`}>{sale.date}</h4>
                              {sale.isOnHold && (
                                <Badge variant="outline" className="bg-orange-500/20 text-orange-700 border-orange-300">
                                  Beklemede
                                </Badge>
                              )}
                              {isCancelled && (
                                <Badge variant="outline" className="bg-destructive/20 text-destructive border-destructive/30">
                                  İptal Edildi
                                </Badge>
                              )}
                              {!isCancelled && (
                                <Badge variant="outline" className={statusConfig.color}>
                                  {statusConfig.label}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Product Names */}
                            {sale.productNames && sale.productNames.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {sale.productNames.map((productName, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {productName}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            
                            {/* Payment Info */}
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span>Toplam: <span className={`font-semibold ${isCancelled ? 'text-destructive/70 line-through' : 'text-foreground'}`}>{sale.total}</span></span>
                              {(sale.paymentStatus !== 'paid' || sale.isOnHold) && (
                                <>
                                  <span>Ödenen: <span className="font-semibold text-foreground">₺{sale.totalPaid.toLocaleString('tr-TR')}</span></span>
                                  {sale.balanceDue > 0 && (
                                    <span>Kalan: <span className="font-semibold text-red-600">₺{sale.balanceDue.toLocaleString('tr-TR')}</span></span>
                                  )}
                                </>
                              )}
                            </div>
                            
                            {/* Note if exists */}
                            {sale.items && (sale.items !== 'Satış detayları' || sale.isOnHold) && (
                              <p className="text-sm text-muted-foreground italic">{sale.items}</p>
                            )}
                          </div>
                          
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              if (sale.isOnHold && sale.linkedSaleId) {
                                // On-hold record linked to a sale - navigate to that sale
                                navigate(`/sales/${sale.linkedSaleId}`);
                              } else if (sale.isOnHold) {
                                // On-hold record without linked sale - navigate to onhold detail page
                                // Check if ID is already in onhold_ format
                                if (sale.id && sale.id.startsWith('onhold_')) {
                                  // Use the ID directly
                                  navigate(`/onhold-detail/${sale.id}`);
                                } else if (sale.customerId && sale.originalIds && sale.originalIds.length > 0) {
                                  // Grouped records - create composite ID: customerId-date
                                  const dateStr = format(sale.date, 'yyyy-MM-dd');
                                  const compositeId = `${sale.customerId}-${dateStr}`;
                                  navigate(`/onhold-detail/${compositeId}`);
                                } else if (sale.originalIds && sale.originalIds.length > 0) {
                                  // Fallback: use first original ID with prefix
                                  navigate(`/onhold-detail/onhold_${sale.originalIds[0]}`);
                                } else {
                                  navigate('/onhold-sale');
                                }
                              } else {
                                // Regular sale
                                navigate(`/sales/${sale.id}`);
                              }
                            }}
                            className="gap-2 shrink-0"
                          >
                            <Eye className="w-4 h-4" />
                            <span className="hidden sm:inline">Detaylar</span>
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Media Tab */}
          <TabsContent value="media">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Fotoğraflar & Medya</CardTitle>
                  {kvkkStatus.type === 'granted' && (visitFaces.length + visitMediaLinks.length) > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {visitFaces.length + visitMediaLinks.length} fotoğraf
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* KVKK Gate */}
                {kvkkStatus.type !== 'granted' ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                    <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                      <Shield className="w-7 h-7 text-muted-foreground" />
                    </div>
                    <div className="space-y-1.5 max-w-sm">
                      <p className="font-medium text-foreground">Fotoğraflar Erişime Kapalı</p>
                      <p className="text-sm text-muted-foreground">
                        {kvkkStatus.type === 'withdrawn'
                          ? 'Müşteri KVKK onayını geri çektiği için fotoğraflar görüntülenemiyor.'
                          : kvkkStatus.type === 'declined' || kvkkStatus.type === 'denied'
                          ? 'Müşteri KVKK onayını reddettiği için fotoğraflar görüntülenemiyor.'
                          : kvkkStatus.loading
                          ? 'KVKK durumu kontrol ediliyor...'
                          : 'Fotoğrafları görüntülemek için müşteriden KVKK onayı alınması gerekiyor.'}
                      </p>
                    </div>
                  </div>
                ) : (() => {
                  // Build a visitId → entered_at map from visits state
                  const visitDateMap = new Map<string, string>();
                  visits.forEach((v) => { if (v.visitedAt) visitDateMap.set(v.id, v.visitedAt); });

                  // Unified photo list
                  type UnifiedPhoto = {
                    id: string;
                    source: 'face' | 'media';
                    visitId: string;
                    dateKey: string;
                    dateLabel: string;
                    time: string;
                    url: string | null;
                  };

                  const allPhotos: UnifiedPhoto[] = [];

                  visitFaces.forEach((face) => {
                    const rawDate = visitDateMap.get(face.visit_id) || face.created_at;
                    const dateKey = rawDate ? new Date(rawDate).toISOString().slice(0, 10) : 'unknown';
                    const dateLabel = rawDate
                      ? new Date(rawDate).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                      : 'Tarih bilinmiyor';
                    allPhotos.push({
                      id: face.id,
                      source: 'face',
                      visitId: face.visit_id,
                      dateKey,
                      dateLabel,
                      time: face.created_at
                        ? new Date(face.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                        : '',
                      url: visitFaceUrls[face.id] ?? null,
                    });
                  });

                  visitMediaLinks.forEach((ml) => {
                    const rawDate = visitDateMap.get(ml.owner_id) || ml.created_at;
                    const dateKey = rawDate ? new Date(rawDate).toISOString().slice(0, 10) : 'unknown';
                    const dateLabel = rawDate
                      ? new Date(rawDate).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                      : 'Tarih bilinmiyor';
                    const { data: urlData } = supabase.storage.from(ml.bucket).getPublicUrl(ml.path);
                    allPhotos.push({
                      id: ml.id,
                      source: 'media',
                      visitId: ml.owner_id,
                      dateKey,
                      dateLabel,
                      time: ml.created_at
                        ? new Date(ml.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                        : '',
                      url: urlData?.publicUrl ?? null,
                    });
                  });

                  if (allPhotos.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                        <Camera className="w-12 h-12 text-muted-foreground opacity-40" />
                        <p className="text-muted-foreground">Henüz fotoğraf yüklenmemiş</p>
                        <p className="text-sm text-muted-foreground opacity-70">Ziyaret sırasında çekilen fotoğraflar burada görünecektir</p>
                      </div>
                    );
                  }

                  // Group by date
                  const groups = new Map<string, { dateLabel: string; visitIds: Set<string>; photos: UnifiedPhoto[] }>();
                  allPhotos.forEach((p) => {
                    if (!groups.has(p.dateKey)) {
                      groups.set(p.dateKey, { dateLabel: p.dateLabel, visitIds: new Set(), photos: [] });
                    }
                    const g = groups.get(p.dateKey)!;
                    g.visitIds.add(p.visitId);
                    g.photos.push(p);
                  });

                  return (
                    <div className="space-y-8">
                      {Array.from(groups.entries())
                        .sort(([a], [b]) => b.localeCompare(a))
                        .map(([dateKey, group]) => (
                          <div key={dateKey}>
                            {/* Date header */}
                            <div className="flex items-center gap-2 mb-4">
                              <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                              <span className="text-sm font-semibold text-foreground capitalize">{group.dateLabel}</span>
                              <span className="text-xs text-muted-foreground">({group.photos.length})</span>
                              <div className="flex-1 h-px bg-border" />
                              {group.visitIds.size === 1 && (
                                <button
                                  onClick={() => navigate(`/visits/${[...group.visitIds][0]}`)}
                                  className="text-xs text-primary hover:underline shrink-0"
                                >
                                  Ziyareti Görüntüle →
                                </button>
                              )}
                            </div>
                            {/* Photo grid */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                              {group.photos.map((photo) => (
                                <div key={photo.id} className="group relative">
                                  <div className="aspect-square rounded-lg overflow-hidden bg-muted ring-1 ring-border">
                                    {photo.url ? (
                                      <img
                                        src={photo.url}
                                        alt="Ziyaret fotoğrafı"
                                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105 cursor-pointer"
                                        onClick={() => photo.url && window.open(photo.url, '_blank')}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
                                      </div>
                                    )}
                                    {photo.time && (
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-end p-2 pointer-events-none">
                                        <span className="text-white text-xs font-medium">{photo.time}</span>
                                      </div>
                                    )}
                                  </div>
                                  <p className="mt-1 text-xs text-muted-foreground text-center">
                                    {photo.source === 'face' ? 'Kamera' : 'Yüklenen'}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customer Notes Tab */}
          <TabsContent value="fields">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Müşteri Notları</CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowAddNoteModal(true)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {customerNotes.length > 0 ? (
                    <div className="space-y-4">
                      {customerNotes.map((note) => (
                        <div key={note.id} className="p-4 rounded-lg border bg-card/50">
                          <div className="flex items-start justify-between mb-2">
                            <p className="text-sm text-muted-foreground">
                              {new Date(note.created_at).toLocaleDateString('tr-TR')} - 
                              {new Date(note.created_at).toLocaleTimeString('tr-TR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            <Button variant="ghost" size="sm">
                              <Edit className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-sm">{note.body}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Henüz not eklenmemiş</p>
                      <Button 
                        variant="outline" 
                        className="mt-2 gap-2"
                        onClick={() => setShowAddNoteModal(true)}
                      >
                        <Plus className="w-4 h-4" />
                        İlk notu ekle
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Müşteri Bilgileri</CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowEditCustomerModal(true)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <Label>Ad Soyad</Label>
                    <span className="col-span-2">{customer?.name}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Label>E-posta</Label>
                    <span className="col-span-2">{customer?.email || 'Belirtilmemiş'}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Label>Telefon</Label>
                    <span className="col-span-2">{customer?.phone || 'Belirtilmemiş'}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Label>Doğum Tarihi</Label>
                    <span className="col-span-2">
                      {customer?.customer_data.birth_date 
                        ? new Date(customer.customer_data.birth_date).toLocaleDateString('tr-TR')
                        : 'Belirtilmemiş'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Label>Yıldönümü</Label>
                    <span className="col-span-2">
                      {customer?.customer_data.anniversary_date 
                        ? new Date(customer.customer_data.anniversary_date).toLocaleDateString('tr-TR')
                        : 'Belirtilmemiş'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Label>Renk Etiketi</Label>
                    <span className="col-span-2">
                      <Badge variant="outline" className={`${colorConfig[customer?.colorTag as keyof typeof colorConfig]?.bgClass || 'bg-gray-500/20'} text-gray-700`}>
                        {colorConfig[customer?.colorTag as keyof typeof colorConfig]?.label || customer?.colorTag || 'Yok'}
                      </Badge>
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <Label>Etiketler</Label>
                    <div className="col-span-2">
                      <CustomerTagList
                        manualTags={customer?.customer_data.tags_manual || []}
                        autoTags={customer?.customer_data.tags_auto || []}
                        showIcons={true}
                        showTooltips={true}
                        size="md"
                      />
                    </div>
                  </div>
                  
                  {/* Categorized Tags Section */}
                  {((customer?.customer_data.tags_manual && customer.customer_data.tags_manual.length > 0) || 
                    (customer?.customer_data.tags_auto && customer.customer_data.tags_auto.length > 0)) && (
                    <>
                      <Separator className="my-4" />
                      <div className="space-y-4">
                        <Label className="text-base font-semibold">Kategorilendirilmiş Etiketler</Label>
                        
                        {/* Manual Tags by Category */}
                        {customer?.customer_data.tags_manual && customer.customer_data.tags_manual.length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-medium text-blue-700">Manuel Etiketler</h4>
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                                {customer.customer_data.tags_manual.length}
                              </Badge>
                            </div>
                            {Object.entries(getTagsByCategory(customer.customer_data.tags_manual, true)).map(([category, tags]) => (
                              <div key={category} className="bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-xs font-medium text-blue-800">
                                    {TAG_CATEGORIES[category as keyof typeof TAG_CATEGORIES] || category}
                                  </span>
                                  <div className="h-px flex-1 bg-blue-200" />
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {tags.map(({ tag, config }) => (
                                    <CustomerTag
                                      key={tag}
                                      tag={tag}
                                      isManual={true}
                                      showIcon={true}
                                      showTooltip={true}
                                      size="sm"
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Auto Tags by Category */}
                        {customer?.customer_data.tags_auto && customer.customer_data.tags_auto.length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-medium text-green-700">Otomatik Etiketler</h4>
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                                {customer.customer_data.tags_auto.length}
                              </Badge>
                            </div>
                            {Object.entries(getTagsByCategory(customer.customer_data.tags_auto, false)).map(([category, tags]) => (
                              <div key={category} className="bg-green-50/50 p-3 rounded-lg border border-green-100">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-xs font-medium text-green-800">
                                    {TAG_CATEGORIES[category as keyof typeof TAG_CATEGORIES] || category}
                                  </span>
                                  <div className="h-px flex-1 bg-green-200" />
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {tags.map(({ tag, config }) => (
                                    <CustomerTag
                                      key={tag}
                                      tag={tag}
                                      isManual={false}
                                      showIcon={true}
                                      showTooltip={true}
                                      size="sm"
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <Card>
              <CardHeader>
                <CardTitle>Zaman Çizelgesi</CardTitle>
              </CardHeader>
              <CardContent>
                {timelineEvents.length > 0 ? (
                  <div className="space-y-4">
                    {timelineEvents.map((event) => {
                      const IconComponent = getTimelineIcon(event.type);
                      const iconColor = getTimelineColor(event.type);
                      
                      return (
                        <div key={event.id} className="flex gap-4 p-4 rounded-lg border bg-card/50 hover:bg-card/70 transition-colors">
                          <div className={`w-10 h-10 rounded-full ${iconColor} flex items-center justify-center flex-shrink-0`}>
                            <IconComponent className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-sm">{event.title}</h4>
                                <p className="text-sm text-muted-foreground truncate">{event.description}</p>
                              </div>
                              <div className="text-right text-xs text-muted-foreground whitespace-nowrap">
                                <p>{new Date(event.date).toLocaleDateString('tr-TR')}</p>
                                <p>{new Date(event.date).toLocaleTimeString('tr-TR', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">Henüz aktivite bulunmuyor</p>
                    <p className="text-sm text-muted-foreground">
                      Müşteri ile yapılan tüm aktiviteler burada görünecektir
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* New Visit Modal */}
        <NewVisitModal
          open={showNewVisitModal}
          onOpenChange={setShowNewVisitModal}
          preselectedCustomer={customer ? {
            id: customer.id,
            first_name: customer.name.split(' ')[0],
            last_name: customer.name.split(' ').slice(1).join(' '),
            email: customer.email,
          } : undefined}
        />

        {/* Add Calendar Event Modal */}
        <AddCalendarEventModal
          open={showAddCalendarModal}
          onOpenChange={setShowAddCalendarModal}
          onEventAdd={handleAddCalendarEvent}
          isAdding={isCreating}
          preselectedCustomer={customer ? {
            id: customer.id,
            name: customer.name,
          } : undefined}
        />

        {/* Edit Customer Modal */}
        <EditCustomerModal
          open={showEditCustomerModal}
          onOpenChange={setShowEditCustomerModal}
          customer={customer?.customer_data || {
            id: '',
            first_name: null,
            last_name: null,
            email: null,
            phone: null,
            birth_date: null,
            anniversary_date: null,
            color: null,
            tags_manual: null,
          }}
          onCustomerUpdated={fetchCustomerData}
        />

        {/* Add Customer Note Modal */}
        <AddCustomerNoteModal
          open={showAddNoteModal}
          onOpenChange={setShowAddNoteModal}
          onNoteAdd={handleAddCustomerNote}
          isAdding={isAddingNote}
        />
      </div>
    </Layout>
  );
};

export default CustomerDetail;